import { AntDesign, Ionicons, Fontisto } from "@expo/vector-icons";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View, ActivityIndicator } from "react-native";
import Animated, {
  useSharedValue,
  withTiming,
  Easing,
  useAnimatedStyle,
} from "react-native-reanimated";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import DepositModal from "@/components/modal/banking/DepositModal";
import { Button, ButtonText } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Menu, MenuItem, MenuItemLabel } from "@/components/ui/menu";
import {
  Modal,
  ModalBackdrop,
  ModalCloseButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import i18n from "@/locales/i18n";
import { sendRequest } from "@/utils/ApiUtil";
import { checkIcon, checkAsset } from "@/utils/transactions/ExchangeCurrencies";

interface CardAccountProps {
  id: string;
  currentBalance: any;
  availableBalance: any;
  depositInstructions: any[];
  type: string;
  lastUpdate: string;
  status: string;
  activeLongPress: () => void;
  clearFilter: () => void;
  isEditing?: object;
}

export default function CardAccount(props: CardAccountProps) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isReceiveHovered, setIsReceiveHovered] = useState(false);
  const [isSendHovered, setIsSendHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);
  const width = useSharedValue(192);

  function checkIsCrypto(type: string) {
    const typeParts = type.split("_");
    if (typeParts.some((part) => part === "USD" || part === "EUR")) {
      return false;
    } else {
      return true;
    }
  }

  useEffect(() => {
    if (globalValues.currentAccountId !== props.id) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [globalValues.currentAccountId]);

  useEffect(() => {
    if (expanded) {
      width.value = withTiming(384, {
        duration: 500,
        easing: Easing.out(Easing.quad),
      });
    } else {
      width.value = withTiming(192, {
        duration: 500,
        easing: Easing.out(Easing.quad),
      });
    }
  }, [expanded]);

  const anymatedStyle = useAnimatedStyle(() => {
    return {
      width: width.value,
    };
  });

  const findAnotherAccount = () => {
    const accounts = globalValues.accountsData.filter(
      (account: any) => account.id !== props.id
    );
    if (accounts.length > 0) {
      dispatch(setForm({ currentAccountId: accounts[0].id }));
    } else {
      dispatch(setForm({ currentAccountId: "" }));
    }
  };

  const deleteAccount = async (id: string) => {
    setIsDeleting(true);
    try {
      await sendRequest(
        "PATCH",
        "/accounts",
        { accountId: id, status: "CLOSED" },
        true
      );
      Toast.show({
        type: "success",
        visibilityTime: 3500,
        props: {
          text1: t("common:toast.success"),
          text2: t("common:toast.subtitle.accountclosed"),
        },
      });
      findAnotherAccount();
      dispatch(setForm({ accountHasChanges: true }));
      setShowDeleteModal(false);
      setIsDeleting(false);
    } catch (error: any) {
      console.error(error);
      setShowDeleteModal(false);
      setIsDeleting(false);
    }
  };

  function formatCurrency(balance: number): string {
    if (isNaN(balance) || balance < 1000) {
      if (checkIsCrypto(props.type)) {
        return `${balance}`;
      } else {
        return `$${balance.toFixed(2)}`;
      }
    }

    const suffixes = ["", "k", "kk", "kkk", "kkkk"];
    const power = Math.floor(Math.log10(balance) / 3);
    const index = Math.min(power, suffixes.length - 1);
    const formattedValue = (balance / Math.pow(10, index * 3)).toFixed(2);

    if (checkIsCrypto(props.type)) {
      return `${formattedValue}${suffixes[index]}`;
    } else {
      return `$${formattedValue}${suffixes[index]}`;
    }
  }

  const checkCardActionIcon = (
    expanded: boolean,
    hovered: boolean,
    editing: boolean
  ) => {
    if (expanded) {
      return (
        <View className="flex-row items-center rounded-full bg-purple1 h-8 w-8 justify-center shadow">
          <Ionicons
            name="checkmark-outline"
            color="#fff"
            fontWeight="bold"
            size={20}
          />
        </View>
      );
    } else if (hovered && !editing) {
      return (
        <View className="flex-row items-center rounded-full bg-purple1 h-8 w-8 justify-center items-center shadow">
          <Ionicons
            name="arrow-forward-outline"
            color="#fff"
            fontWeight="100"
            size={20}
          />
        </View>
      );
    } else if (!hovered && !editing) {
      return (
        <View className="flex-row items-center rounded-full bg-white h-8 w-8 justify-center shadow">
          <Ionicons
            name="arrow-forward-outline"
            color="#000000"
            fontWeight="100"
            size={20}
          />
        </View>
      );
    } else if (editing) {
      return (
        <View className="flex-row items-center rounded-full bg-white h-8 w-8 justify-center shadow">
          <Fontisto
            name="arrow-move"
            color="#000000"
            fontWeight="100"
            size={20}
          />
        </View>
      );
    }
  };

  return (
    <Pressable
      onPress={() => {
        setExpanded(!expanded);
        if (globalValues.currentAccountId !== props.id) {
          dispatch(setForm({ currentAccountId: props.id }));
          dispatch(setForm({ currentAccountAssetType: props.type }));
          dispatch(setForm({ transactionsHasChanges: true }));
          dispatch(setForm({ transactionsIsLoading: true }));
          dispatch(setForm({ isTransactionsFiltered: false }));
          props.clearFilter();
        }
      }}
      onLongPress={() => {
        if (!expanded) {
          props.activeLongPress();
        }
      }}
      delayLongPress={1000}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      disabled={props.status !== "OPEN"}
    >
      <Animated.View
        className="bg-white1 transition duration-200 hover:shadow-md flex-col h-40 rounded-lg p-4 mr-3 relative shadow lg:h-48 lg:mt-0"
        style={anymatedStyle}
      >
        <View className="z-50 flex-row items-center justify-between absolute top-0 left-0 right-0 p-4">
          {expanded ? (
            <View>
              <Menu
                trigger={({ ...triggerProps }) => {
                  return (
                    <Pressable {...triggerProps}>
                      <Ionicons
                        name="ellipsis-vertical"
                        className="z-50 text-midgray hover:text-darkergray"
                        size={18}
                      />
                    </Pressable>
                  );
                }}
              >
                <MenuItem
                  onPress={() => setShowDeleteModal(true)}
                  key="Delete"
                  textValue={t("common:terms.delete")}
                >
                  <Ionicons
                    name="trash-outline"
                    size={16}
                    className="mr-2 text-darkergray"
                  />
                  <MenuItemLabel size="sm" style={{ color: "#919191" }}>
                    {t("common:terms.delete")}
                  </MenuItemLabel>
                </MenuItem>
              </Menu>
              <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                  setShowDeleteModal(false);
                }}
              >
                <ModalBackdrop />
                <ModalContent>
                  <ModalHeader>
                    <Heading size="lg">
                      {t("home/home:card.deleteaccount")}
                    </Heading>
                    <ModalCloseButton>
                      <AntDesign name="close" size={24} color="black" />
                    </ModalCloseButton>
                  </ModalHeader>
                  <ModalBody>
                    <Text>{`${t("home/home:card.deleteaccountwarning")}`}</Text>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      variant="outline"
                      size="sm"
                      action="secondary"
                      className="mr-2"
                      onPress={() => {
                        setShowDeleteModal(false);
                      }}
                    >
                      <ButtonText>{t("common:terms.cancel")}</ButtonText>
                    </Button>
                    {isDeleting ? (
                      <View>
                        <ActivityIndicator
                          size="small"
                          color="#5D0CBA"
                          className="mx-2"
                        />
                      </View>
                    ) : (
                      <Button
                        variant="solid"
                        size="sm"
                        action="primary"
                        className="bg-purple1 mr-2"
                        onPress={() => deleteAccount(props.id)}
                      >
                        <ButtonText className="text-white1">
                          {t("common:terms.confirm")}
                        </ButtonText>
                      </Button>
                    )}
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </View>
          ) : (
            <View>{checkIcon(props.type)}</View>
          )}
          {expanded && (
            <View
              className={`${expanded ? "flex-col items-center" : "hidden"}`}
            >
              <View className="flex-row items-center gap-2 my-1">
                <View className={`${!expanded ? "hidden" : ""}`}>
                  {checkIcon(props.type, 20)}
                </View>
                <Text className="text-sm font-light">
                  {checkAsset(props.type)}
                </Text>
              </View>
              <Text
                numberOfLines={1}
                className="text-sm font-light text-midgray"
              >
                {`${t("common:placeholder.lastupdate")}: ${props.lastUpdate}`}
              </Text>
            </View>
          )}
          {checkCardActionIcon(expanded, isHovered, props.isEditing[props.id])}
        </View>
        <View
          className={`flex-grow items-center justify-center ${
            expanded ? "flex-col" : ""
          }`}
        >
          <View className="absolute items-center justify-center w-full">
            {props.status === "OPEN" ? (
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                className="text-2xl font-normal"
              >
                {`${
                  expanded
                    ? checkIsCrypto(props.type)
                      ? props.availableBalance
                      : new Intl.NumberFormat(i18n.language, {
                          style: "currency",
                          currency: "USD",
                        }).format(props.availableBalance)
                    : formatCurrency(props.availableBalance)
                }`}
              </Text>
            ) : (
              <View className="flex-col items-center mt-12">
                <Text className="text-xs text-center text-darkergray">
                  {t("common:placeholder.settingaccount")}
                </Text>
                <Text className="text-xs text-center text-midgray">
                  {t("common:placeholder.adviceaccount")}
                </Text>
                <ActivityIndicator
                  className="mt-2"
                  size="small"
                  color="#5D0CBA"
                />
              </View>
            )}
          </View>
        </View>
        <View className="absolute bottom-0 left-0 w-full">
          {expanded && (
            <View className={`${expanded ? "flex-row w-full" : "hidden"}`}>
              <Pressable
                className="w-1/2"
                onPress={() => setShowDepositModal(true)}
                onHoverIn={() => setIsReceiveHovered(true)}
                onHoverOut={() => setIsReceiveHovered(false)}
                disabled={props.status !== "OPEN"}
              >
                <View
                  style={[
                    {
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.3,
                      shadowRadius: isReceiveHovered ? 3 : 1,
                      overflow: "hidden",
                    },
                  ]}
                  className="flex-row items-center justify-center p-1 rounded-bl-lg border border-gray"
                >
                  <AntDesign
                    name="arrowup"
                    size={16}
                    style={{
                      transform: [{ rotate: `225deg` }],
                    }}
                    color="#000000"
                  />
                  <Text className="text-sm ml-2">
                    {t("common:terms.receive")}
                  </Text>
                </View>
              </Pressable>
              <Pressable
                className="w-1/2"
                onPress={() => router.push("/home/transaction/contacts")}
                onHoverIn={() => setIsSendHovered(true)}
                onHoverOut={() => setIsSendHovered(false)}
                disabled={props.status !== "OPEN"}
              >
                <View
                  style={[
                    {
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.3,
                      shadowRadius: isSendHovered ? 3 : 1,
                      overflow: "hidden",
                    },
                  ]}
                  className="flex-row items-center justify-center p-1 rounded-br-lg border border-gray"
                >
                  <AntDesign
                    name="arrowup"
                    size={16}
                    style={{
                      transform: [{ rotate: `45deg` }],
                    }}
                    color="#000000"
                  />
                  <Text className="text-sm ml-2">{t("common:terms.send")}</Text>
                </View>
              </Pressable>
            </View>
          )}
        </View>
        <DepositModal
          visible={showDepositModal}
          onClose={() => setShowDepositModal(false)}
          onClick={(state) => setShowDepositModal(state)}
          depositInstructions={props.depositInstructions}
          accountType={checkAsset(props.type)}
          accountId={props.id}
        />
      </Animated.View>
    </Pressable>
  );
}
